import React from 'react';

// Renderer markdown minimo per la knowledge base: titoli, paragrafi, liste,
// blocchi di codice, riga orizzontale, e inline grassetto / codice / link.
// Fatto in casa perché il frontend non ha librerie markdown e la regola del
// progetto vieta di aggiungerne senza approvazione.

export type Blocco =
  | { tipo: 'h1' | 'h2' | 'h3' | 'p' | 'pre'; testo: string }
  | { tipo: 'ul' | 'ol'; voci: string[] }
  | { tipo: 'hr' };

export function parseBlocchi(md: string): Blocco[] {
  const righe = md.replace(/\r\n/g, '\n').split('\n');
  const blocchi: Blocco[] = [];
  let paragrafo: string[] = [];
  let lista: { tipo: 'ul' | 'ol'; voci: string[] } | null = null;
  let codice: string[] | null = null;

  const chiudiParagrafo = () => {
    if (paragrafo.length) { blocchi.push({ tipo: 'p', testo: paragrafo.join(' ') }); paragrafo = []; }
  };
  const chiudiLista = () => {
    if (lista) { blocchi.push(lista); lista = null; }
  };

  for (const riga of righe) {
    if (codice) {
      if (riga.trim() === '```') { blocchi.push({ tipo: 'pre', testo: codice.join('\n') }); codice = null; }
      else codice.push(riga);
      continue;
    }
    const t = riga.trim();
    if (t.startsWith('```')) { chiudiParagrafo(); chiudiLista(); codice = []; continue; }
    if (!t) { chiudiParagrafo(); chiudiLista(); continue; }
    if (t === '---' || t === '***') { chiudiParagrafo(); chiudiLista(); blocchi.push({ tipo: 'hr' }); continue; }

    const h = t.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      chiudiParagrafo(); chiudiLista();
      blocchi.push({ tipo: (`h${h[1].length}`) as 'h1' | 'h2' | 'h3', testo: h[2].trim() });
      continue;
    }
    const ul = t.match(/^[-*]\s+(.*)$/);
    const ol = t.match(/^\d+[.)]\s+(.*)$/);
    if (ul || ol) {
      chiudiParagrafo();
      const tipo = ul ? 'ul' : 'ol';
      if (!lista || lista.tipo !== tipo) { chiudiLista(); lista = { tipo, voci: [] }; }
      lista.voci.push((ul || ol)![1].trim());
      continue;
    }
    chiudiLista();
    paragrafo.push(t);
  }
  chiudiParagrafo();
  chiudiLista();
  if (codice) blocchi.push({ tipo: 'pre', testo: codice.join('\n') });
  return blocchi;
}

export function renderInline(testo: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let ultimo = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(testo))) {
    if (m.index > ultimo) out.push(testo.slice(ultimo, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) out.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={k++}>{tok.slice(1, -1)}</code>);
    else {
      const link = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/)!;
      out.push(<a key={k++} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]}</a>);
    }
    ultimo = m.index + tok.length;
  }
  if (ultimo < testo.length) out.push(testo.slice(ultimo));
  return out;
}

export function MarkdownMinimo({ testo }: { testo: string }) {
  const blocchi = parseBlocchi(testo);
  return (
    <div className="bop-markdown">
      {blocchi.map((b, i) => {
        switch (b.tipo) {
          case 'h1': return <h1 key={i} className="bop-markdown__h1">{renderInline(b.testo)}</h1>;
          case 'h2': return <h2 key={i} className="bop-markdown__h2">{renderInline(b.testo)}</h2>;
          case 'h3': return <h3 key={i} className="bop-markdown__h3">{renderInline(b.testo)}</h3>;
          case 'p': return <p key={i} className="bop-markdown__p">{renderInline(b.testo)}</p>;
          case 'pre': return <pre key={i} className="bop-markdown__pre">{b.testo}</pre>;
          case 'hr': return <hr key={i} className="bop-markdown__hr" />;
          case 'ul': return <ul key={i} className="bop-markdown__ul">{b.voci.map((v, j) => <li key={j}>{renderInline(v)}</li>)}</ul>;
          case 'ol': return <ol key={i} className="bop-markdown__ol">{b.voci.map((v, j) => <li key={j}>{renderInline(v)}</li>)}</ol>;
          default: return null;
        }
      })}
    </div>
  );
}
